
export enum UserRole {
  STUDENT = 'student',
  EDUCATION_CONSULTANT = 'education_consultant',
  TEACHER = 'teacher',
  PARENT = 'parent', // Veli rolü
  ADMIN = 'admin', // Site yöneticisi
}

export type ApprovalStatus = 'pending' | 'approved' | 'rejected';


export interface User {
  id: number | string;
  name: string;
  role: UserRole;
  email?: string;
  password?: string;
  phoneNumber?: string;
  packageStatus?: 'active' | 'expired' | 'pending' | 'none'; // Optional to allow Student checks after generic User return
}

export interface Resource {
  id: number;
  name: string;
  type: 'Konu' | 'Soru' | 'Video';
  subject?: string;
  examType?: string;
}

export enum ScaleLevel {
  LOW = 'Düşük',
  MEDIUM = 'Orta',
  HIGH = 'Yüksek',
  EFFICIENT = 'Verimli',
  PARTIALLY_EFFICIENT = 'Kısmen Verimli',
  INEFFICIENT = 'Verimsiz',
  WEAK = 'Zayıf',
  GOOD = 'İyi',
}

export interface ScaleHistoryItem {
  date: string;
  score: number;
  level: ScaleLevel;
  systemComment: string;
  subScales?: Record<string, number>; // Added for direct access and dictionary usage
  details?: {
    subScales?: {
      name: string;
      score: number;
      level: ScaleLevel;
      comment: string;
    }[];
    roadmapResults?: any;
  };
  academicReport?: any;
  timeReport?: any;
}

export interface ScaleDetail {
  name?: string;
  maxScore?: number; // Added maxScore
  current?: ScaleHistoryItem;
  history?: ScaleHistoryItem[];
  consultantNotes?: string;
  // Fields used in onboarding phase
  date?: string;
  score?: number;
  level?: ScaleLevel;
  systemComment?: string;
  details?: {
    subScales?: {
      name: string;
      score: number;
      level: ScaleLevel;
      comment: string;
    }[];
    roadmapResults?: any;
  };
  academicReport?: any; // Kalıcı detaylı analiz için
  timeReport?: any; // Kalıcı yaşam analizi için
}

export interface ScaleResult {
  studyHabits: ScaleDetail;
  attention: ScaleDetail;
  anxiety: ScaleDetail;
  readingComprehension: ScaleDetail;
  careerInterest: ScaleDetail;
  parentStudyHabits?: ScaleDetail; // Veli gözlem ölçeği - Benim Ders Çalışma Veli Ölçeği
}

export interface ReadingComprehensionQuestion {
  id: number;
  paragraph: string;
  question: string;
  options: string[];
  correctAnswer: string;
  rationale: string;
  hint: string;
}

export type DayOfWeek = 'Pazartesi' | 'Salı' | 'Çarşamba' | 'Perşembe' | 'Cuma' | 'Cumartesi' | 'Pazar';

export type TaskStatus = 'pending' | 'completed' | 'incomplete' | 'in_progress';

export type TaskCategory = 'Konu Çalışması' | 'Soru Çözümü' | 'Tekrar' | 'TYT Denemesi' | 'AYT Denemesi' | 'Branş Denemesi' | 'Video' | 'Diğer';

export type CompletionType = 'topic_studied' | 'questions_solved' | 'review_done' | 'fully_completed';

export interface PlanTopic {
  id: number;
  name: string;
  topicResource: string;
  questionResource: string;
  status: TaskStatus;
  day: DayOfWeek;
  time: string;
  points: number;
  assignedBy?: string;
  category?: TaskCategory;
  completionType?: CompletionType;
  completedAt?: string;
  examType?: 'TYT' | 'AYT' | 'YDT' | 'LGS' | '5. Sınıf' | '6. Sınıf' | '7. Sınıf' | '8. Sınıf' | 'Diğer';
  topicKey?: string; // Original topic name from EXAM_TOPICS for tracking
  estimatedDuration?: number; // Tahmini süre (dakika)
  duration?: string;
  questionCount?: number;
}

export interface WeeklyPlan {
  week: number;
  startDate?: string; // YYYY-MM-DD format
  endDate?: string;   // YYYY-MM-DD format
  subjects: {
    [key: string]: {
      topics: PlanTopic[];
    };
  };
}

export interface QuestionTopicMap {
  questionNumber: number;
  subject: string;
  topic: string;
  correctAnswer: 'A' | 'B' | 'C' | 'D' | 'E';
}

export interface ExamLibraryItem {
  id: number;
  name: string;
  examType: 'TYT' | 'AYT' | 'LGS' | 'YDT';
  questions: QuestionTopicMap[];
}

export interface TopicAnalysisItem {
  topic: string;
  subject: string;
  correct: number;
  incorrect: number;
  empty: number;
  net: number;
  averageTime?: number; // Soru başına harcanan süre
  difficultyPerformance?: { [level: number]: number }; // Zorluk bazlı başarı oranı
}

export interface TopicQuestion {
  questionText: string;
  questionImage: string | null;
  options: string[];
  correctAnswer: number;
  skillType: 'interpretation' | 'application' | 'reasoning' | 'inference' | 'factual';
  difficultyLevel: number;
  solutionImage: string | null;
  solutionVideoId: string | null;
}

export interface TopicQuiz {
  id: string;
  subject: string;
  topic: string;
  examType: string;
  questions: TopicQuestion[];
  isActive: boolean;
  createdAt: string;
}

export interface Class {
  id: string;
  name: string;
  teacherId: string;
  studentIds: string[];
  grade?: string;
  code: string;
  createdAt?: string;
}

export interface QuizResult {
  id: string;
  studentId: string;
  teacherId?: string;
  classId?: string;
  quizId: string;
  subject: string;
  topic: string;
  examType: string;
  answers: {
    questionIndex: number;
    selectedAnswer: number;
    isCorrect: boolean;
    timeSpentSeconds?: number;
  }[];
  correctCount: number;
  incorrectCount: number;
  unansweredCount: number;
  topicNet: number;
  totalQuestions: number;
  score: number;
  timeSpentSeconds: number;
  completedAt: string;
}

export interface TrialExamResult {
  id: number;
  name: string;
  examType: 'TYT' | 'AYT' | 'LGS' | 'YDT';
  date: string;
  scores: {
    [key: string]: {
      correct: number;
      incorrect: number;
      empty: number;
      net: number;
    };
  };
  topicAnalysis: TopicAnalysisItem[];
  studentAnswers?: { [questionNumber: string]: string };
  sessionId?: string; // Link to the detailed exam session
  examId?: number; // Link to the original exam if needed
  questions?: ExamQuestion[]; // Store questions for review mode
  totalNet: number;
  totalCorrect: number;
  totalIncorrect: number;
  totalEmpty: number;
  durationUsed?: number; // Sınavda harcanan süre (saniye cinsinden)
}

export interface ExamQuestion {
  id: number;
  examType: 'TYT' | 'AYT' | 'YDT' | 'LGS';
  section: string; // e.g., 'Matematik', 'Türkçe'
  questionNumber: number;
  text: string;
  image?: string;
  options: {
    label: string; // 'A', 'B', 'C', 'D', 'E'
    text?: string;
    image?: string;
  }[];
  correctOption: string;
  topic?: string;
  solution?: string;
}

export interface ExamSession {
  id: string;
  studentId: number;
  examType: 'TYT' | 'AYT' | 'YDT' | 'LGS';
  examName: string;
  startTime: string;
  endTime?: string;
  duration: number; // in minutes
  remainingTime: number; // in seconds
  answers: { [questionId: number]: string }; // questionId -> optionLabel ('A')
  status: 'active' | 'completed' | 'paused';
  currentQuestionIndex: number;
  currentSection: string;
  bookmarks: number[]; // Array of marked question IDs
}

export interface DetailedExamResult extends TrialExamResult {
  session: ExamSession;
  questions: ExamQuestion[]; // Store the questions to review later
}

export interface YearlyRoadmap {
  month: string;
  subjects: {
    name: string;
    topics: string[];
  }[];
}

export interface NotificationItem {
  id: number;
  userId: number;
  title?: string;
  message: string;
  read: boolean;
  type: 'info' | 'success' | 'warning';
  date: string;
  action?: {
    type: 'evaluate' | 'view';
    appointmentId?: number;
  };
}

export interface Message {
  id: number;
  senderId: number;
  senderName: string;
  receiverId?: number;
  content: string;
  timestamp: string;
  read: boolean;
  isAdmin?: boolean;
}

// Updated Appointment Interface
export interface Appointment {
  id: number;
  studentId: number | string;
  studentName: string;
  educatorId?: number | string; // New field to track consultant/teacher ID
  date: string;
  time: string;
  meetLink: string;
  notes: string;
  createdBy?: number | string; // ID of the user who created it
  type: 'consultant' | 'teacher'; // New field to distinguish appointment type
  subject?: string; // New field for lesson subject (e.g., Mathematics)
  status?: 'pending' | 'approved' | 'scheduled' | 'completed' | 'cancelled' | 'missed'; // Appointment status
  summary?: string; // Meeting summary written by consultant/teacher
  summaryDate?: string; // Date when summary was written
  summaryBy?: string; // Name of person who wrote the summary
  teacherName?: string; // Name of teacher for teacher appointments
  teacherId?: number | string; // ID of assigned teacher for teacher appointments
  // Rating fields - Student feedback after meeting
  rating?: {
    score: number;        // 1-5 yıldız
    comment?: string;     // Opsiyonel yorum
    ratedAt: string;      // Değerlendirme tarihi (ISO)
    ratedBy: string;      // Öğrenci adı
  };
}

export type Department = 'Sayısal' | 'Eşit Ağırlık' | 'Sözel' | 'Yabancı Dil';

export interface CompletedTopicDetail {
  date: string;
  topicResourceName: string;
  questionResourceName: string | string[]; // Birden fazla soru kaynağı desteklenir
  topicCompleted?: boolean; // Konu kaynağı tamamlandı mı
  questionCompleted?: boolean; // Soru kaynağı tamamlandı mı
  questionStats?: {
    solved: number;
    correct: number;
    incorrect: number;
  };
}



export interface EducationConsultant extends User {
  role: UserRole.EDUCATION_CONSULTANT;
  studentIds: string[];
  appointments: Appointment[];
  blockedSlots?: BlockedSlot[];
  approvalStatus?: ApprovalStatus; // Onay durumu
  // New Profile Fields
  iban?: string;
  phoneNumber?: string;
  meetingRate?: number;
  totalEarnings?: number;
  education?: { degree: string, school: string, year: string }[];
  certificates?: { name: string, issuer: string, date: string }[];
  city?: string;
  avatar?: string;
}

// BlockedSlot Interface - for marking unavailable time slots
export interface BlockedSlot {
  id: number;
  date: string;      // YYYY-MM-DD format
  time: string;      // HH:MM format
  reason?: string;   // Optional reason
}

// New Teacher Interface
export interface Teacher extends User {
  role: UserRole.TEACHER;
  branch: string; // e.g., Matematik
  studentIds?: string[];
  appointments: Appointment[];
  blockedSlots?: BlockedSlot[];
  approvalStatus?: ApprovalStatus; // Onay durumu
  avatar?: string;
  // Profile Fields
  iban?: string;
  phoneNumber?: string;
  education?: { degree: string, school: string, year: string }[];
  certificates?: { name: string, issuer: string, date: string }[];
  city?: string;
}

export type QuestionStatus = 'pending' | 'answered' | 'rejected';

export interface QuestionQuery {
  id: number;
  studentId: number;
  image: string; // Base64 or URL
  subject: string;
  topic?: string;
  text?: string;
  status: QuestionStatus;
  answer?: string;
  answerImage?: string; // Teacher's solution image
  teacherName?: string;
  date: string;
}

// Kariyer Envanteri Sonuç Tipi
export interface CareerInventoryResult {
  id: string;
  completedAt: string;
  // Holland Tipleri (R, I, A, S, E, C)
  hollandTypes: {
    type: string;
    name: string;
    score: number;
    percentage: number
  }[];
  // Yetenekler
  topAbility: {
    type: string;
    name: string;
    score: number;
    percentage: number;
  };
  // Değerler
  topValue: {
    type: string;
    name: string;
    score: number;
  };
  // Tutum
  topAttitude: {
    type: string;
    name: string;
    score: number;
  };
  // Çalışma Tarzı
  topWorkStyle: {
    type: string;
    name: string;
    score: number;
  };
  // Önerilen Kariyer Yolları
  recommendedCareers: {
    title: string;
    description: string;
    matchRate: number;
    tags: string[];
  }[];
  // Ham puanlar
  rawScores: Record<string, number>;
}

// Saved target for quick access
export interface SavedTarget {
  id: string; // Unique ID for this saved target
  name: string; // Custom name given by the student
  targetId: string; // YÖK Atlas program ID
  targetUni: string;
  targetDept: string;
  type: string; // SAY, EA, SOZ, DIL
  obp: number;
  savedAt: string; // ISO date string
}

export interface Student extends User {
  role: UserRole.STUDENT;
  school: string;
  grade: string;
  className?: string;
  department: Department;
  goal: string;
  dailyStudyHours: number;
  onboarded: boolean;
  academicInterests?: string[];
  freeTime?: string; // New: Daily free time range
  motivationLevel?: number; // New: 1-10 scale
  initialNets?: { tyt?: number; ayt?: number; ydt?: number }; // New: Initial exam scores
  strongSubjects?: string[];
  weakSubjects?: string[];
  // New personal info fields
  parentPhone?: string; // Veli telefon numarası
  parentEmail?: string; // Veli e-posta adresi
  city?: string; // Yaşadığı şehir
  points: number;
  streak: number;
  bestStreak?: number;
  lastCompletionDate: string | null;
  resources: Resource[];
  completedTopics: string[];
  completedTopicDetails: Record<string, CompletedTopicDetail>;
  completedRoadmapTasks?: string[];
  scales: ScaleResult;
  weeklyPlans: WeeklyPlan[];
  trialExams: TrialExamResult[];
  consultantFeedback: string[];
  messages: Message[];
  targetUniversity?: string;
  targetDepartment?: string;
  targetUni?: string; // Keeping for backward compatibility if needed
  targetDept?: string; // Keeping for backward compatibility if needed
  targetId?: string;
  targetName?: string; // Custom name for the saved target
  savedTargets?: SavedTarget[]; // Array of saved targets for quick access
  obp?: number;
  questions: QuestionQuery[]; // New field for Ask and Learn
  orders?: StudentOrder[]; // Student orders for resource tracking
  careerInventoryResults?: CareerInventoryResult[]; // Kariyer envanteri sonuçları
  habitProgram?: { // 21 günlük alışkanlık programı
    startDate: string;
    tasks: any[];
    currentDay: number;
  };

  // New Hybrid Planning Fields
  planningMode?: PlanningMode; // Aktif mod
  consultantTasks?: ConsultantTask[]; // Danışman görevleri
  priorityTopics?: PriorityTopic[]; // Öncelikli konular
  blacklistedTopics?: string[]; // Engellenen konular
  dailyPlans?: DailyPlan[]; // Otopilot planları
  smartPlannerConfig?: SmartPlannerConfig; // Otopilot ayarları
  assignedConsultantId?: string | number; // Atanan danışman ID'si
  assignedTeacherIds?: (string | number)[]; // Atanan öğretmen ID'leri
  stats?: {
    registrationDate?: string;
    solvedQuestions?: number;
    taskCompletionRate?: number;
    videoViewRate?: number;
  };
  // Package/Subscription fields
  activePackageId?: string;
  packageName?: string;
  packagePurchaseDate?: string;
  packageExpiryDate?: string;
  packageStatus?: 'active' | 'expired' | 'pending' | 'none';
  meetingQuotas?: {
    consultancy?: {
      monthlyUsed?: number;
      monthlyTotal?: number;
      [key: string]: any;
    };
    privateTutoring?: {
      monthlyUsed?: number;
      monthlyTotal?: number;
      [key: string]: any;
    };
    nextResetDate?: string;
    lastResetDate?: string;
    [key: string]: any;
  };
  // Kaynak bazlı konu takibi: { [subject]: { [topicKey]: { [resourceId]: boolean } } }
  topicTracking?: Record<string, Record<string, Record<string, boolean>>>;
}

export type TabType = 'personal' | 'education';

export interface Parent extends User {
  role: UserRole.PARENT;
  studentIds: string[]; // Takip ettiği öğrenci ID'leri
  avatar?: string;
  notifications?: {
    sms: boolean;
    email: boolean;
  };
}


export interface Homework {
  id: string;
  title: string;
  description: string;
  dueDate: string;
  corporateId: string;
  studentIds: string[];
  status: 'active' | 'completed';
  createdAt?: string;
  updatedAt?: string;
}

// ============ ADMIN PANEL TYPES ============

// Grade Level Types
// 'İlk Aşama': maarif modelinin yeni sınavı (9-10. sınıf, TYT'nin yerine) —
// components/EduAnaliz/utils/ilkAsama.js
export type GradeLevel = '5. Sınıf' | '6. Sınıf' | '7. Sınıf' | '8. Sınıf' | '9. Sınıf' | '10. Sınıf' | '11. Sınıf' | '12. Sınıf' | 'Mezun' | 'TYT' | 'AYT' | 'YDT' | 'LGS' | 'İlk Aşama';
export type TopicSource = 'maarif' | 'yks' | 'lgs';

export interface GradeTopicConfig {
  id: string;
  grade: GradeLevel;
  source: TopicSource;
  examType?: 'TYT' | 'AYT' | 'YDT' | 'LGS' | 'İlk Aşama';
  /** AYT alanı; boş = alan belirtilmemiş, her alanda görünür. */
  alan?: 'SAY' | 'EA' | 'SÖZ' | 'DİL';
  subject: string;
  topics: string[];
  // Konunun yanındaki etiketler — üçü de KONU ADIYLA anahtarlanır ve konu
  // KİMLİĞİNE girmez (bkz. server/models/GradeTopic.js). Kaynak: okulun
  // yıllık planı.

  /** Konu adı -> ünite/tema. { "İyonik Bağ": "ÇEŞİTLİLİK" } */
  topicTema?: Record<string, string>;
  /** Konu adı -> öğrenme çıktıları; bir konuda birden fazla olabilir. */
  topicCikti?: Record<string, string[]>;
  /** Konu adı -> yıllık plandaki hafta aralığı. Kalıcı olan NUMARA, tarih değil. */
  topicHafta?: Record<string, { ilk: number; son: number; etiket?: string }>;
  createdAt?: string;
  updatedAt?: string;
}

// Video Types
export interface VideoLesson {
  id: string;
  grade: GradeLevel;
  subject: string;
  topic: string;
  order: number;
  title: string;
  description?: string;
  youtubeUrl: string;
  youtubeVideoId: string;
  duration: number;
  createdAt: string;
  updatedAt: string;
}

export interface VideoProgress {
  id: string;
  studentId: string;
  videoId: string;
  watchedPercentage: number;
  completed: boolean;
  completedAt?: string;
  lastWatchedAt: string;
}

// Authorization Types
export type AuthorizationType = 'student_view' | 'teacher_view' | 'consultant_view' | 'parent_view';

export interface AuthorizedUser {
  id: string;
  userId: number;
  userName: string;
  userRole: UserRole;
  authorizations: AuthorizationType[];
  grantedBy: string;
  grantedAt: string;
  isActive: boolean;
}

// Accounting Types
export interface StudentPayment {
  id: string;
  studentId: string;
  studentName: string;
  yearlyFee: number;
  paidAmount: number;
  remainingAmount: number;
  payments: {
    id: string;
    amount: number;
    paymentDate: string;
    paymentMethod: 'nakit' | 'havale' | 'kredi_karti' | 'otomatik';
    description?: string;
  }[];
  status: 'tam_odendi' | 'kismi_odendi' | 'odenmedi' | 'gecikti';
  createdAt: string;
}

export interface TeacherLesson {
  id: string;
  teacherId: string;
  studentId: string;
  studentName: string;
  date: string;
  duration: number;
  subject: string;
  topic?: string;
  hourlyRate: number;
  earning: number;
  status: 'tamamlandi' | 'iptal';
}

export interface TeacherEarning {
  id: string;
  teacherId: string;
  teacherName: string;
  hourlyRate: number;
  monthlyThresholdHours: number;
  currentMonthHours: number;
  currentMonthEarning: number;
  thresholdReached: boolean;
  totalHours: number;
  totalEarning: number;
  paidAmount: number;
  pendingAmount: number;
  lessons: TeacherLesson[];
  paymentHistory: {
    id: string;
    amount: number;
    paymentDate: string;
    month: string;
  }[];
}

export interface ConsultantSession {
  id: string;
  consultantId: string;
  studentId: string;
  studentName: string;
  date: string;
  duration: number;
  sessionType: 'gorusme' | 'analiz' | 'planlama';
  notes?: string;
  sessionRate: number;
  earning: number;
  status: 'tamamlandi' | 'iptal';
}

export interface ConsultantMeeting {
  id: string;
  consultantId: string;
  consultantName: string;
  sessionRate: number;
  currentMonthSessions: number;
  currentMonthDuration: number;
  currentMonthEarning: number;
  totalSessions: number;
  totalDuration: number;
  totalEarning: number;
  paidAmount: number;
  pendingAmount: number;
  sessions: ConsultantSession[];
  paymentHistory: {
    id: string;
    amount: number;
    paymentDate: string;
    month: string;
  }[];
}

export interface AccountingSummary {
  totalStudents: number;
  totalYearlyFees: number;
  totalStudentPaid: number;
  totalStudentPending: number;
  totalTeachers: number;
  totalTeacherHours: number;
  totalTeacherPayable: number;
  totalTeacherPaid: number;
  totalConsultants: number;
  totalConsultantSessions: number;
  totalConsultantPayable: number;
  totalConsultantPaid: number;
  totalResourceSales: number;
  totalResourceRevenue: number;
  totalRevenue: number;
  totalExpense: number;
  netIncome: number;
}

// Resource Types
export interface ResourceProduct {
  id: string;
  name: string;
  type: 'Konu' | 'Soru' | 'Video';
  subject: string;
  description?: string;
  price: number;
  discount?: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ResourceSale {
  id: string;
  studentId: string;
  studentName: string;
  resourceId: string;
  resourceName: string;
  resourceType: 'Konu' | 'Soru' | 'Video';
  originalPrice: number;
  discountApplied: number;
  finalPrice: number;
  purchaseDate: string;
  paymentMethod: 'kredi_karti' | 'havale' | 'bakiye';
  status: 'tamamlandi' | 'iptal' | 'iade';
  refundDate?: string;
  refundReason?: string;
}

// Student Order Types
export type OrderStatus = 'pending' | 'confirmed' | 'preparing' | 'shipped' | 'delivered' | 'cancelled';

export interface OrderItem {
  id: number;
  name: string;
  type: 'Konu' | 'Soru' | 'Video';
  subject: string;
  price: number;
  quantity: number;
}

export interface StudentOrder {
  id: number;
  orderNumber: string;
  items: OrderItem[];
  totalAmount: number;
  status: OrderStatus;
  paymentMethod: 'cash' | 'card' | 'transfer';
  shippingInfo: {
    fullName: string;
    phone: string;
    address: string;
    city: string;
  };
  createdAt: string;
  updatedAt: string;
  trackingNumber?: string;
  estimatedDelivery?: string;
  deliveredAt?: string;
  statusHistory: {
    status: OrderStatus;
    date: string;
    note?: string;
  }[];
}

// YÖK Atlas Types
export interface YokAtlasEntry {
  id: string;
  universityName: string;
  departmentName: string;
  fieldType: 'SAY' | 'EA' | 'SOZ' | 'DIL';
  city: string;
  quota: number;
  placementData: {
    year: number;
    minScore: number;
    maxScore: number;
    minRank: number;
    maxRank: number;
    requiredTytNet: number;
    requiredAytNet: number;
  }[];
  updatedAt: string;
}

// Topic History Types
export interface TopicHistoryEntry {
  id: string;
  examType: 'TYT' | 'AYT';
  lesson: string;
  topic: string;
  history: { [year: number]: number };
  totalLast7Years: number;
  average: number;
  importanceScore: number;
  updatedAt: string;
}

// Support Ticket Types
export type TicketStatus = 'open' | 'in_progress' | 'resolved' | 'closed';
export type TicketPriority = 'low' | 'medium' | 'high' | 'urgent';
export type TicketCategory = 'technical' | 'billing' | 'general' | 'complaint';

export interface TicketResponse {
  id: string;
  responderId: string;
  responderName: string;
  message: string;
  createdAt: string;
}

export interface SupportTicket {
  id: string;
  userId: number;
  userName: string;
  userRole: UserRole;
  userEmail: string;
  subject: string;
  category: TicketCategory;
  message: string;
  priority: TicketPriority;
  status: TicketStatus;
  responses: TicketResponse[];
  createdAt: string;
  updatedAt: string;
}

// ============ SMART DAILY PLANNER TYPES ============

// Planlama Modu
export type PlanningMode = 'manual' | 'autopilot';

// Danışman Görevi
export interface ConsultantTask {
  id: number;
  subject: string;
  topic: string;
  assignedBy: string;
  assignedDate: string;
  dueDate?: string;
  priority: 'low' | 'medium' | 'high';
  topicResource?: string;
  questionResource?: string;
  notes?: string;
  status: 'pending' | 'completed';
}

// Öncelikli Konu
export interface PriorityTopic {
  subject: string;
  topic: string;
  priority: 'critical' | 'high' | 'medium';
  addedBy: 'consultant' | 'ai';
  addedDate: string;
  expiryDate?: string;
}

// Çalışma Temposu
export type StudyTempo = 'low' | 'medium' | 'high';

// Planlama Modu
export type PlannerMode = 'school' | 'yks' | 'lgs';

// Günlük Görev Durumu
export type DailyTaskStatus = 'locked' | 'available' | 'in_progress' | 'completed' | 'incomplete';

// Günlük Görev
export interface DailyTask {
  id: string;
  date: string; // YYYY-MM-DD
  subject: string;
  topic: string;
  examType: 'TYT' | 'AYT' | 'YDT' | 'LGS' | '5. Sınıf' | '6. Sınıf' | '7. Sınıf' | '8. Sınıf' | 'Okul';
  taskType: 'video' | 'question' | 'both';
  videoId?: string;
  videoTitle?: string;
  videoDuration?: number; // dakika
  questionBankId?: string;
  questionBankTitle?: string;
  questionCount?: number;
  status: DailyTaskStatus;
  completedAt?: string;
  videoCompleted?: boolean;
  questionsCompleted?: boolean;
  points: number;
  order: number; // Gün içindeki sıralama
  isMakeup?: boolean; // Telafi görevi mi?
  originalDate?: string; // Telafi ise orijinal tarih
  addedBy?: 'ai' | 'consultant' | 'teacher'; // Kimin eklediğini işaretle
  quizId?: string;
  teacherId?: string;
  classId?: string;
  isUrgent?: boolean; // Acil görev mi?
}

// Günlük Plan
export interface DailyPlan {
  date: string; // YYYY-MM-DD
  dayOfWeek: DayOfWeek;
  tasks: DailyTask[];
  totalPoints: number;
  completedPoints: number;
  isUnlocked: boolean;
  unlockTime: string; // HH:MM format (01:00)
}

// Akıllı Planlayıcı Konfigürasyonu
export interface SmartPlannerConfig {
  mode: PlannerMode;
  tempo: StudyTempo;
  dailySubjectCount: number; // 2-7 arası
  gradeLevel: string;
  department?: Department;
  startDate: string;
  tytCompletionDays: number; // 75 gün = 2.5 ay
  aytCompletionDays: number; // 135 gün = 4.5 ay
  currentPhase: 'tyt' | 'ayt' | 'review'; // Hangi aşamada
  isActive: boolean;
}

// Telafi Görevi
export interface MakeupTask {
  id: string;
  originalTaskId: string;
  originalDate: string;
  task: DailyTask;
  scheduledDate?: string;
  scheduledTime?: string;
  status: 'pending' | 'scheduled' | 'completed';
}

export interface CurriculumTask {
  title?: string; // Görev adı (Konu, Soru, Tekrar vb.)
  subject: string;
  topic: string;
  examType: 'TYT' | 'AYT' | 'YDT' | 'LGS' | '5. Sınıf' | '6. Sınıf' | '7. Sınıf' | '8. Sınıf';
  taskType: 'video' | 'question' | 'both';
  questionCount: number;
  videoDuration: number;
}

export interface StudyPlanCurriculum {
  id: string;
  grade: string;
  department: string;
  weekNumber: number;
  days: {
    [key in DayOfWeek]?: {
      tasks: CurriculumTask[];
    };
  };
  isActive: boolean;
}
